export interface PageMetricTip {
  label: string;
  interpretation: string;
}

export interface PageInfoConfig {
  path: string;
  title: string;
  shortTitle: string;
  group: string;
  subtitle: string;
  whatItRepresents: string;
  whyItMatters: string;
  keyMetrics: PageMetricTip[];
  actionablePlaybook: string[];
  playbookSectionId?: string;
  proTip?: string;
}

export const PAGE_INFO_MAP: Record<string, PageInfoConfig> = {
  '/': {
    path: '/',
    title: 'Market Dashboard',
    shortTitle: 'Dashboard',
    group: 'Overview',
    subtitle: 'Executive cockpit consolidating broad-market breadth, volume shocks, institutional accumulation, and sector rotation.',
    whatItRepresents:
      'Dalal Street’s aggregate institutional posture. Instead of relying solely on headline Nifty 50 movements—which can be heavily skewed by just 2 or 3 mega-caps—this desk gauges the health of 2,000+ NSE equities using moving average breadth, net advance/decline participation, and market regime detection.',
    whyItMatters:
      'Individual swing trade setups have an 80%+ higher win rate when taken in the direction of the broad market tide. Trading in a deteriorating market regime turns high-quality breakouts into whipsaws.',
    keyMetrics: [
      {
        label: 'Market Stance / Regime',
        interpretation: 'Bullish (risk-on; favorable for breakouts), Neutral (selective stock-picking), or Bearish/Cautious (defensive capital preservation and stop-loss tightening).',
      },
      {
        label: '21 EMA Breadth Gauge',
        interpretation: 'Percentage of all NSE equities holding above their short-term 21 EMA. Values above 50% indicate expanding market health.',
      },
      {
        label: 'Advance / Decline Balance',
        interpretation: 'Compares positive vs. negative session closes across the entire exchange to detect broad participation.',
      },
      {
        label: 'Top Sector Drivers',
        interpretation: 'Pinpoints which industry baskets are attracting net delivery accumulation versus experiencing capital outflow.',
      },
    ],
    actionablePlaybook: [
      'Check the Market Stance first thing every morning: aggressive long entries only during Bullish regimes.',
      'Align individual stock selections with the top 3–5 accumulating sectors highlighted on the dashboard.',
      'When the market regime turns Bearish, raise stop-losses to protect open profits and pause initiating new breakout positions.',
    ],
    playbookSectionId: 'dashboard',
    proTip:
      'If Nifty 50 prints a fresh high while broad breadth drops and declines exceed advances, you are observing a "narrow market divergence"—a classic warning of an impending market shakeout.',
  },

  '/overview': {
    path: '/overview',
    title: 'Accumulation Ladder',
    shortTitle: 'Ladder',
    group: 'Market Internals & Breadth',
    subtitle: 'Multi-day institutional persistence tiers (1D to 15D+ streaks) and 21 > 50 > 200 EMA golden trend alignment.',
    whatItRepresents:
      'Multi-day institutional commitment tiers. Institutions managing billions cannot buy a stock in a single session; they accumulate quietly across multiple consecutive days with heavy delivery volume. This desk organizes the market into an accumulation persistence ladder—from early 1-day sparks to high-conviction 10D+ and 15D+ streaks.',
    whyItMatters:
      'Single-day volume surges can be random speculative noise or retail intraday churn. Stocks appearing on consecutive 5D, 10D, and 15D+ streak tiers reflect systematic institutional absorption that typically leads to sustained multi-week markup phases.',
    keyMetrics: [
      {
        label: 'Streak Tiers (1D to 15D+)',
        interpretation: 'Consecutive market sessions where a stock fulfilled institutional accumulation criteria (Volume > 30-day average AND Demat Delivery ≥ 50%).',
      },
      {
        label: '10D+ High Conviction',
        interpretation: 'Highest institutional sponsorship. Stocks in this bucket consistently find buyers at their rising 21 EMA and exhibit minimal downside volatility.',
      },
      {
        label: '21 > 50 > 200 EMA Filter',
        interpretation: 'Filters the ladder for stocks in a pristine "Golden Stack" technical uptrend where short, medium, and long-term moving averages are in synchronized upward alignment.',
      },
      {
        label: 'Stock Capsule Colors',
        interpretation: 'Orange = 21>50>200 EMA stack, Pink = Price above all EMAs, Green = Price above 21 EMA, Neutral = Consolidating below 21 EMA.',
      },
    ],
    actionablePlaybook: [
      'Prioritize reviewing the 10D+ and 15D+ streak cards first to spot Dalal Street’s strongest institutional favorites.',
      'Toggle the "21 > 50 > 200 EMA" filter tile to eliminate counter-trend bounces and focus solely on structural market leaders.',
      'Look for stocks on the ladder that pull back on light volume into their 21 EMA as prime low-risk swing entries.',
    ],
    playbookSectionId: 'overview',
    proTip:
      'The highest-probability swing trades occur when an Orange (21>50>200 EMA) stock in a 5D+ streak forms an orderly 2-to-3 day base contraction on declining volume.',
  },

  '/market-breadth': {
    path: '/market-breadth',
    title: 'Market Breadth',
    shortTitle: 'Breadth',
    group: 'Market Internals & Breadth',
    subtitle: 'Unified market internals hub consolidating EMA moving average structure, Advance/Decline issue participation, and institutional volume flow.',
    whatItRepresents:
      'Dalal Street’s internal structural momentum and participation breadth. Consolidates 3 complementary dimensions of market health: moving average golden stack alignment (21, 50, 200 EMA), exchange-wide advance/decline momentum thrusts, and cumulative institutional accumulation versus distribution turnover.',
    whyItMatters:
      'Headline index movements can be deceptive when dominated by heavyweight index stocks. True market turning points, high-probability breakout environments, and distribution regimes reveal themselves first in broad-market internal breadth.',
    keyMetrics: [
      {
        label: '% Above 21 / 50 / 200 EMA',
        interpretation: 'Short, intermediate, and long-term trend health barometer across 2,000+ NSE stocks.',
      },
      {
        label: 'Golden Stack Alignment',
        interpretation: 'Stocks with 21 > 50 > 200 EMA structure representing sustained institutional uptrends.',
      },
      {
        label: 'Advance / Decline Ratio',
        interpretation: 'Ratio of advancing to declining issues. Readings > 2.0 signal broad buying thrusts; < 0.7 signal distribution.',
      },
      {
        label: '4%+ Momentum Thrusts',
        interpretation: 'Measures high-velocity institutional impulse days indicating the start of powerful new trend legs.',
      },
      {
        label: 'Advancing Volume %',
        interpretation: 'Percentage of total turnover committed to advancing equities. Readings > 70% confirm institutional accumulation.',
      },
    ],
    actionablePlaybook: [
      'Toggle across EMA Structure, Advance/Decline, and Volume Flow tabs to verify market regime alignment before sizing up swing breakout trades.',
      'Watch for Breadth Thrusts: rapid surges from oversold (<25%) to expansion (>50%) mark high-conviction entry environments.',
      'When breadth exceeds 80% or advancing volume diverges from index prices, tighten trailing stops and take profits into strength.',
    ],
    playbookSectionId: 'market-breadth',
    proTip:
      'The highest-probability breakout setups occur when 21 EMA breadth is between 35% and 70% and rising, accompanied by an Adv/Dec ratio > 2.0 and advancing volume > 70%.',
  },

  '/index-heatmap': {
    path: '/index-heatmap',
    title: 'Index Sector Heatmap',
    shortTitle: 'Heatmap',
    group: 'Market Internals & Breadth',
    subtitle: 'Real-time relative strength, dispersion, and performance hierarchy across all NSE benchmark and thematic indices.',
    whatItRepresents:
      'A multi-timeframe performance and dispersion heatmap across all NSE indices (Nifty 50, Bank, IT, Pharma, Auto, Metals, FMCG, Realty, PSU Banks, Midcap, Smallcap). It reveals where institutional capital is concentrating and which sectors are lagging.',
    whyItMatters:
      'Sector rotation drives up to 80% of individual equity alpha. Buying the best stock in the leading sector dramatically outperforms buying an equivalent setup in a lagging sector.',
    keyMetrics: [
      {
        label: '1D / 5D / 1M Return',
        interpretation: 'Relative percentage gain/loss over daily, weekly, and monthly horizons to identify rotational momentum.',
      },
      {
        label: 'Advance / Decline Ratio',
        interpretation: 'Constituent participation ratio confirming whether an index move is broad-based or driven by a lone heavy-weight.',
      },
      {
        label: 'Relative Strength vs Nifty 50',
        interpretation: 'Positive spread indicates an outperforming industry basket; negative spread indicates an underperforming basket.',
      },
    ],
    actionablePlaybook: [
      'Focus swing trade long setups strictly on the top 2–3 outperforming green sector clusters.',
      'Avoid or hedge positions in sectors displaying deep red momentum or accelerating underperformance.',
      'Look for nascent sector turns where a beaten-down index transitions from red to amber/green with expanding constituent breadth.',
    ],
    playbookSectionId: 'sector',
    proTip:
      'When Midcap and Smallcap indices outpace Nifty 50 while broad market breadth rises, risk appetite is at its peak—ideal for aggressive momentum breakouts.',
  },

  '/mtf-positioning': {
    path: '/mtf-positioning',
    title: 'MTF Positioning & Leverage',
    shortTitle: 'MTF',
    group: 'Market Internals & Breadth',
    subtitle: 'Margin Trading Facility (MTF) book size, leverage expansion, and retail/HNI positioning across NSE equities.',
    whatItRepresents:
      'SEBI/NSE Margin Trading Facility (MTF) ledger data tracking leveraged retail and HNI book exposure across individual stocks and sector groups.',
    whyItMatters:
      'Excessive MTF leverage often marks speculative tops and creates vulnerability to sharp cascading margin-call unwinds during market pullbacks.',
    keyMetrics: [
      {
        label: 'MTF Book Value (₹ Cr)',
        interpretation: 'Total outstanding margin book financed in the stock.',
      },
      {
        label: 'MTF % of Market Cap / Free Float',
        interpretation: 'High percentage (>3-5%) indicates elevated leverage concentration vulnerable to margin liquidations.',
      },
      {
        label: 'MTF Book 5D / 1M Change',
        interpretation: 'Rapid expansion indicates aggressive speculative buildup; rapid contraction indicates leverage capitulation.',
      },
    ],
    actionablePlaybook: [
      'Be cautious holding breakout longs in stocks with extreme MTF concentration during broad market corrections.',
      'Look for accumulation setups where institutional delivery rises while MTF leverage remains low or contracting—this indicates high institutional ownership quality.',
      'During market sell-offs, stocks with the highest MTF leverage often experience sharp, fast capitulation bottoms when leverage is washed out.',
    ],
    playbookSectionId: 'mtf',
    proTip:
      'When price breaks down below key moving averages with high MTF concentration, broker margin calls can amplify selling pressure into a vicious spiral.',
  },

  '/ema-chart': {
    path: '/ema-chart',
    title: 'EMA Breadth',
    shortTitle: 'Breadth',
    group: 'Market Internals & Breadth',
    subtitle: 'Percentage of NSE stocks holding above key Exponential Moving Averages (21, 50, 100, 200 EMA).',
    whatItRepresents:
      'Internal structural momentum across entire market cycles. By tracking the percentage of all exchange-listed stocks trading above their short-term (21 EMA), intermediate (50 EMA), and long-term (200 EMA) benchmarks, this chart reveals market cycle turning points.',
    whyItMatters:
      'Markets swing between oversold panic and overbought euphoria. Knowing where the current market stands on the breadth spectrum prevents you from buying at cycle tops or panic-selling at cycle bottoms.',
    keyMetrics: [
      {
        label: '% Above 21 EMA',
        interpretation: 'Short-term momentum barometer. Highly sensitive to daily swing dynamics and early regime reversals.',
      },
      {
        label: '% Above 50 EMA',
        interpretation: 'Intermediate trend baseline. Sustained moves above 50% confirm healthy institutional participation across sectors.',
      },
      {
        label: 'Oversold Panic (< 20%)',
        interpretation: 'Capitulation zone. Extreme fear creates high-reward swing reversal opportunities in high-delivery market leaders.',
      },
      {
        label: 'Euphoria Zone (> 80%)',
        interpretation: 'Overheated market. Risk-reward for buying fresh breakouts deteriorates; trailing stops should be tightened.',
      },
    ],
    actionablePlaybook: [
      'Watch for "Breadth Thrusts": rapid upward crosses from under 25% to above 50% signal the inception of major institutional bull legs.',
      'When breadth drops below 20%, prepare your buy watchlist rather than panicking—market bottoms form here.',
      'When breadth exceeds 80%, take partial profits into strength and avoid initiating new aggressive breakout buys.',
    ],
    playbookSectionId: 'ema-chart',
    proTip:
      'The sweet spot for swing trading breakouts is when 21 EMA breadth is between 35% and 70% and rising.',
  },

  '/count': {
    path: '/count',
    title: 'Advance / Decline',
    shortTitle: 'Adv / Dec',
    group: 'Market Internals & Breadth',
    subtitle: 'Daily count of advancing versus declining equities across the entire National Stock Exchange.',
    whatItRepresents:
      'Exchange-wide participation breadth. It plots the absolute count of NSE stocks closing positive (green) versus negative (red) for each session, verifying whether index gains are backed by broad participation or driven by narrow manipulation.',
    whyItMatters:
      'Headline indices can easily rise if 3 heavyweight stocks gain while 1,500 mid/small-caps decline. The Advance/Decline count strips away index weighting to reveal true retail and institutional market sentiment.',
    keyMetrics: [
      {
        label: 'Advancing Stocks',
        interpretation: 'Total number of stocks closing higher than their previous session close.',
      },
      {
        label: 'Declining Stocks',
        interpretation: 'Total number of stocks closing lower than their previous session close.',
      },
      {
        label: 'Adv/Dec Ratio > 2.0',
        interpretation: 'Strong institutional impulse day. More than 2 stocks rise for every 1 that falls, indicating broad buying thrust.',
      },
      {
        label: 'Adv/Dec Ratio < 0.7',
        interpretation: 'Broad selling pressure and institutional distribution across multiple sectors.',
      },
    ],
    actionablePlaybook: [
      'Look for 2:1 or 3:1 Adv/Dec thrust sessions to confirm aggressive buying appetite across small and mid caps.',
      'Watch for positive divergence: if the Nifty index tests a prior low but advancing stocks increase, an intermediate bottom is developing.',
      'Never short an advancing market when the Adv/Dec ratio is above 2.0.',
    ],
    playbookSectionId: 'count',
    proTip:
      'A true trend day typically exhibits at least 1,400+ advances against fewer than 600 declines.',
  },

  '/accumulation-chart': {
    path: '/accumulation-chart',
    title: 'A/D Volume Trend',
    shortTitle: 'A/D Trend',
    group: 'Market Internals & Breadth',
    subtitle: 'Aggregate trading volume and institutional delivery committed to advancing versus declining stocks.',
    whatItRepresents:
      'Volume conviction behind price movement. Price can easily be nudged on low liquidity, but genuine institutional demand demands massive capital commitment. This chart tracks whether advancing volume or declining volume dominates total market turnover.',
    whyItMatters:
      'A market rally accompanied by declining volume is often a deceptive "low-volume squeeze" that collapses upon encountering resistance. High advancing volume confirms institutional buying support.',
    keyMetrics: [
      {
        label: 'Advancing Volume %',
        interpretation: 'Percentage of total exchange volume traded in advancing equities. Readings above 70% indicate institutional accumulation.',
      },
      {
        label: 'Declining Volume Spikes',
        interpretation: 'Heavy turnover in declining stocks indicates aggressive institutional distribution and selling pressure.',
      },
      {
        label: 'Cumulative Volume Flow',
        interpretation: 'The multi-week trajectory of net advancing volume versus declining volume.',
      },
    ],
    actionablePlaybook: [
      'Confirm breakout days: high-probability breakout follow-through requires advancing volume to exceed 70% of total turnover.',
      'Identify distribution days: if the market index is flat or mildly up while declining volume surges, smart money is offloading into retail bids.',
    ],
    playbookSectionId: 'accumulation-chart',
    proTip:
      'When advancing volume surges above 80% on a day following an oversold breadth reading, you have a textbook Institutional Follow-Through Day.',
  },

  '/industry': {
    path: '/industry',
    title: 'Industry Analysis',
    shortTitle: 'Industry',
    group: 'Market Internals & Breadth',
    subtitle: 'Heatmap, breadth rankings, and multi-timeframe capital flow deltas (1D, 5D, 20D) across 70+ industry groups.',
    whatItRepresents:
      'Thematic sector rotation and smart money migration. Institutional funds do not buy isolated individual stocks; they deploy capital in entire industry themes (e.g. Defence, Power, Railways, Private Banks, Capital Goods). This desk ranks every industry by net delivery volume and multi-week accumulation deltas.',
    whyItMatters:
      'Over 80% of a stock’s performance is determined by its sector’s relative strength. Trading the leading stock in the top-ranked accumulating sector provides a powerful institutional tailwind.',
    keyMetrics: [
      {
        label: 'Accumulated %',
        interpretation: 'Percentage of constituent stocks in the sector currently meeting institutional accumulation criteria.',
      },
      {
        label: '1D, 5D & 20D Deltas',
        interpretation: 'Rate of change in institutional money flow over 1-day, 1-week, and 1-month horizons, revealing nascent sector rotations.',
      },
      {
        label: '% Above 21 EMA',
        interpretation: 'Internal sector breadth showing what proportion of sector members maintain active short-term upward momentum.',
      },
      {
        label: 'Delivered Value (₹ Cr)',
        interpretation: 'Total rupee value physically absorbed as Demat delivery within that specific industry.',
      },
    ],
    actionablePlaybook: [
      'Sort by 5D or 20D Accumulated Delta to find sectors quietly hoovering capital before mainstream headlines notice.',
      'Click on any sector row to drill down into its individual constituent stocks.',
      'Select the top 2–3 leading stocks within the top-ranked accumulating industry as your prime breakout candidates.',
    ],
    playbookSectionId: 'industry',
    proTip:
      'Never trade a breakout in a sector with a negative 20-day accumulation delta—institutional selling in the peer group will cap its upside.',
  },

  '/sector': {
    path: '/sector',
    title: 'Sector Explorer & Drilldown',
    shortTitle: 'Sectors',
    group: 'Research & Intelligence',
    subtitle: 'Institutional sector drilldown cockpit: sectoral benchmark chart, sub-sector breakdown, and constituent stock ledger.',
    whatItRepresents:
      'Deep-dive analytical tape for each NSE sector, combining sectoral candlestick charts, EMA posture, delivered turnover trends, and constituent stock signals.',
    whyItMatters:
      'Understanding internal sector dynamics reveals whether an entire group is advancing under institutional accumulation or masking weakness with a few mega-caps.',
    keyMetrics: [
      {
        label: 'Sector Breadth (> 21 EMA)',
        interpretation: 'Percentage of stocks in the sector trading above their short-term 21-day EMA trend filter.',
      },
      {
        label: 'Golden Stack Alignment',
        interpretation: 'Stocks with 21 EMA > 50 EMA > 200 EMA in structured institutional markup.',
      },
      {
        label: 'Delivered Flow (₹ Cr)',
        interpretation: 'Net capital permanently delivered into demat accounts within this sector today.',
      },
    ],
    actionablePlaybook: [
      'Select leading sectors with expanding > 21 EMA breadth to find the best breakout setups.',
      'Filter constituents by Golden Stack (21>50>200) or Accumulation to locate institutional leaders.',
    ],
    playbookSectionId: 'sectors',
  },

  '/sector-explorer': {
    path: '/sector-explorer',
    title: 'Sector Explorer',
    shortTitle: 'Sector',
    group: 'Market Internals & Breadth',
    subtitle: 'Deep-dive rotation matrix tracking leadership shifts, relative strength, and capital flow across market sectors.',
    whatItRepresents:
      'Cross-sector rotation and relative strength matrix. Compares sector performance against the benchmark Nifty 500 across multiple timeframes to pinpoint which sectors are leading, weakening, lagging, or improving.',
    whyItMatters:
      'Institutions rotate capital out of extended sectors into freshly consolidating groups. Identifying this rotation early enables entering high-growth setups near base pivots.',
    keyMetrics: [
      {
        label: 'Relative Strength (RS)',
        interpretation: 'Performance of the sector relative to the benchmark Nifty index.',
      },
      {
        label: 'Rotation Quadrants',
        interpretation: 'Leading (strongest momentum & trend), Weakening (losing momentum), Lagging (underperforming), Improving (accelerating upward).',
      },
    ],
    actionablePlaybook: [
      'Focus attention on sectors transitioning from the "Improving" quadrant into the "Leading" quadrant.',
      'Scale out of stocks belonging to sectors descending into the "Weakening" quadrant.',
    ],
    playbookSectionId: 'sectors',
    proTip:
      'When defensive sectors (FMCG, Pharma, IT) lead the matrix while high-beta sectors lag, the market is undergoing a risk-off rotation.',
  },

  '/opportunities': {
    path: '/opportunities',
    title: 'Breakout Scanner',
    shortTitle: 'Breakout Scanner',
    group: 'Setups & Scanners',
    subtitle: 'Algorithmic scanner identifying high-probability long and short setups nearing breakout pivot levels with volume validation.',
    whatItRepresents:
      'Curated institutional swing trading setups. Scans the universe for equities consolidating tightly near multi-week resistance pivots with expanding delivery volume and favorable moving average posture.',
    whyItMatters:
      'Eliminates guesswork and endless chart scrolling by isolating actionable trading pivots that are ready to trigger within 1 to 3 sessions.',
    keyMetrics: [
      {
        label: 'Approaching Breakout',
        interpretation: 'Stocks trading within 2%–3% of key pivot resistance showing pre-breakout volume accumulation.',
      },
      {
        label: 'Confirmed Breakout',
        interpretation: 'Stocks closing decisively above resistance with volume at least 1.5x above their 30-day average.',
      },
      {
        label: 'Super Setups',
        interpretation: 'Highest-confluence setups where 21>50>200 EMA alignment, top-decile sector flow, and 70%+ delivery volume converge.',
      },
      {
        label: 'Pivot Price',
        interpretation: 'The critical multi-week resistance level that triggers institutional momentum when broken.',
      },
    ],
    actionablePlaybook: [
      'Review Approaching Breakouts in the evening to set alerts or prepare buy-stop orders for the following market session.',
      'Confirm entry only when session volume is tracking above average with positive price expansion.',
      'Place initial stop-loss just below the rising 21 EMA or the swing low of the consolidation base.',
    ],
    playbookSectionId: 'opportunities',
    proTip:
      'The cleanest breakouts occur from tight, low-volatility consolidations where volume contracted sharply prior to the breakout day.',
  },

  '/volumeshockers': {
    path: '/volumeshockers',
    title: 'Volume Shockers',
    shortTitle: 'V-Shockers',
    group: 'Setups & Scanners',
    subtitle: 'Real-time detector for unusual intraday volume explosions (≥ 2x average) indicating fresh institutional footprint.',
    whatItRepresents:
      'Unusual institutional volume footprints. When an equity trades 200% to 1,000%+ of its normal 30-day average volume, it signifies material institutional positioning, earnings surprises, or major order wins.',
    whyItMatters:
      'Volume precedes price. Large institutional blocks cannot be hidden; an explosive volume surge is Dalal Street’s clearest early warning that big money is taking a position.',
    keyMetrics: [
      {
        label: 'Volume Multiple (x Avg)',
        interpretation: 'Ratio of current session volume to the 30-day average volume. ≥ 2x is significant; ≥ 5x is exceptional.',
      },
      {
        label: 'Delivery %',
        interpretation: 'Filters genuine institutional accumulation (≥ 50%) from speculative retail intraday day-trading (< 35%).',
      },
      {
        label: 'Delivered Value (₹ Cr)',
        interpretation: 'Absolute rupee value taken into demat accounts. Values > ₹50 Cr confirm tier-1 institutional involvement.',
      },
    ],
    actionablePlaybook: [
      'Filter for Volume Shockers with Delivery % ≥ 60% and positive session close.',
      'Add top names to your Watchlist. If the stock closes in the upper 25% of its daily range, look for a swing entry on a next-day 21 EMA pullback.',
    ],
    playbookSectionId: 'volume-shockers',
    proTip:
      'A volume shocker that surges 3x volume but prints only 18% delivery is retail churn. The same stock on 2.5x volume with 75% delivery is massive institutional accumulation.',
  },

  '/volume-streaks': {
    path: '/volume-streaks',
    title: 'Volume Streaks',
    shortTitle: 'V-Streaks',
    group: 'Setups & Scanners',
    subtitle: 'Stocks printing consecutive trading sessions of above-average volume and sustained delivery.',
    whatItRepresents:
      'Persistent, multi-day institutional volume campaigns. While a volume shocker captures a single-day surge, volume streaks identify stocks with relentless volume expansion day after day.',
    whyItMatters:
      'A multi-day volume streak indicates an institution actively building a substantial position through algorithmic execution algorithms (e.g. VWAP/TWAP orders).',
    keyMetrics: [
      {
        label: 'Consecutive Days',
        interpretation: 'Number of back-to-back sessions trading significantly above 30-day average volume.',
      },
      {
        label: 'Streak Delivery %',
        interpretation: 'Average delivery percentage maintained throughout the streak period.',
      },
    ],
    actionablePlaybook: [
      'Stocks with 3 to 5-day volume streaks often enter strong momentum trends. Look to enter on brief intraday pullbacks.',
    ],
    playbookSectionId: 'volume-streaks',
    proTip:
      'When a volume streak begins right at a multi-month base breakout, it frequently marks the start of a multi-week runaway move.',
  },

  '/price-movers': {
    path: '/price-movers',
    title: '30D Price Movers',
    shortTitle: 'Price Movers',
    group: 'Setups & Scanners',
    subtitle: 'Leading momentum performers and lagging stocks ranked by 30-day percentage performance.',
    whatItRepresents:
      'Medium-term relative strength leaders and laggards. Ranks the entire universe by 30-day percentage price change to highlight market leaders and structural underperformers.',
    whyItMatters:
      'Momentum tends to persist over 1 to 3-month horizons. Identifying the top 5% performers allows you to trade with the strongest leaders rather than bottom-fishing laggards.',
    keyMetrics: [
      {
        label: '30-Day % Change',
        interpretation: 'Net percentage gain or loss over the trailing 30 calendar days.',
      },
      {
        label: 'Moving Average Alignment',
        interpretation: 'Shows whether the price mover is holding structural support above its 21, 50, and 200 EMA.',
      },
    ],
    actionablePlaybook: [
      'Focus on top-decile 30-day gainers that are consolidating constructively in tight flags rather than chasing vertical runups.',
    ],
    playbookSectionId: 'price-movers',
    proTip:
      'Market leaders during the first 30 days of a market breadth thrust usually remain the biggest winners for the entire market cycle.',
  },

  '/accumulation': {
    path: '/accumulation',
    title: 'Accumulation List',
    shortTitle: 'Accumulation',
    group: 'Setups & Scanners',
    subtitle: 'Master scanner cataloging all stocks meeting institutional accumulation criteria on the selected date.',
    whatItRepresents:
      'Daily institutional accumulation master registry. Lists every NSE stock that fulfilled both accumulation conditions: `Volume > 30-day average` AND `Demat Delivery ≥ 50%`.',
    whyItMatters:
      'Acts as your raw institutional pipeline. Every stock that institutions quietly absorbed today appears on this list with its delivered turnover and sector classification.',
    keyMetrics: [
      {
        label: 'Delivered Quantity & %',
        interpretation: 'Shares permanently moved into demat accounts versus traded intraday.',
      },
      {
        label: 'Delivery Value (₹ Cr)',
        interpretation: 'Rupee turnover settled into demat accounts, highlighting large-cap vs mid-cap institutional sponsorship.',
      },
      {
        label: 'EMA Status',
        interpretation: 'Instant badge showing whether the stock is holding above its 21 EMA or in a 21>50>200 EMA stack.',
      },
    ],
    actionablePlaybook: [
      'Sort by Delivered Value (₹ Cr) to identify where the largest institutional funds deployed capital today.',
      'Bookmark candidates with Orange (21>50>200 EMA) capsules to your personal Watchlist.',
    ],
    playbookSectionId: 'accumulation',
    proTip:
      'Stocks that consistently appear on the Accumulation List 3+ times in a single week are being actively absorbed by institutional buyers.',
  },

  '/overboughtsold': {
    path: '/overboughtsold',
    title: 'Overbought / Oversold',
    shortTitle: 'OB / OS',
    group: 'Setups & Scanners',
    subtitle: 'Mean-reversion scanner identifying equities stretched at statistical extremes relative to their moving averages.',
    whatItRepresents:
      'Statistical price stretch and mean-reversion scanner. Flags equities trading at extreme percentage deviations above or below their 21 EMA and 50 EMA.',
    whyItMatters:
      'Stocks stretched too far above their 21 EMA have high probability of sharp mean-reversion pullbacks, while stocks depressed far below their moving averages at major support offer attractive contrarian bounce setups.',
    keyMetrics: [
      {
        label: 'Deviation from 21 EMA (%)',
        interpretation: 'Degree of stretch away from short-term equilibrium.',
      },
      {
        label: 'Overbought Threshold (> 15%–20%)',
        interpretation: 'Vulnerable to profit-taking and consolidation; poor risk-reward for initiating new long positions.',
      },
      {
        label: 'Oversold Threshold (< -15%–20%)',
        interpretation: 'Extended to the downside; potential for sharp short-covering or relief bounce.',
      },
    ],
    actionablePlaybook: [
      'Avoid buying breakouts in stocks that are already >15% stretched above their 21 EMA.',
      'Look for oversold stocks in fundamentally strong sectors that form bullish reversal candles at historical 200 EMA support.',
    ],
    playbookSectionId: 'overbought-oversold',
    proTip:
      'The safest way to trade an overbought stock is to wait for it to mean-revert to touch its rising 21 EMA before buying.',
  },

  '/history': {
    path: '/history',
    title: 'Stock Explorer',
    shortTitle: 'History',
    group: 'Research',
    subtitle: 'Single-stock terminal displaying historical delivery trends, moving average postures, and institutional accumulation logs.',
    whatItRepresents:
      'Comprehensive single-stock diagnostic terminal. Allows you to search any NSE-listed stock to inspect its full historical price action, delivery percentages, volume multiples, accumulation days, and EMA postures over time.',
    whyItMatters:
      'Provides complete context before entering a trade. You can verify whether a stock has a history of genuine institutional delivery support or is prone to speculative pump-and-dump spikes.',
    keyMetrics: [
      {
        label: 'Historical Accumulation Sessions',
        interpretation: 'Past sessions where volume expanded with delivery ≥ 50%, visible as gold markers on price charts.',
      },
      {
        label: '30-Day Moving Average Trajectory',
        interpretation: 'Tracks whether delivery percentage is trending up or down over 1 to 6-month horizons.',
      },
      {
        label: 'Multi-EMA Alignment',
        interpretation: 'Visualizes price position relative to 21, 50, 100, and 200 EMAs.',
      },
    ],
    actionablePlaybook: [
      'Always inspect a stock’s delivery trend before executing a swing trade: ensure institutional buyers have a track record of supporting the stock.',
      'Check if recent accumulation days clustered at key support levels or breakout pivots.',
    ],
    playbookSectionId: 'history',
    proTip:
      'Stocks that hold their 21 EMA on light volume after a cluster of accumulation days are the highest probability swing setups.',
  },

  '/watchlist': {
    path: '/watchlist',
    title: 'Watchlist',
    shortTitle: 'Watchlist',
    group: 'Research',
    subtitle: 'Personalized priority radar for tracking active setups, key technical pivots, and catalyst disclosures.',
    whatItRepresents:
      'Your personalized active trading radar. Save high-potential breakout setups, volume shockers, and accumulation candidates to monitor their daily delivery health, mini sparklines, and trigger levels in one unified view.',
    whyItMatters:
      'Prevents high-conviction ideas from slipping through the cracks. Keeps your focus locked on the 10–20 best setups rather than getting distracted by intraday market noise.',
    keyMetrics: [
      {
        label: 'Session Delivery % & Volume',
        interpretation: 'Monitors whether watchlist candidates are experiencing continued institutional sponsorship.',
      },
      {
        label: 'Sparkline Trend',
        interpretation: 'Rapid visual inspection of recent price trajectory and institutional accumulation dots.',
      },
    ],
    actionablePlaybook: [
      'Maintain a focused list of 15 to 25 top-quality candidates across leading sectors.',
      'Remove stocks that lose their 21 EMA or fail breakout pivots with heavy distribution volume.',
    ],
    playbookSectionId: 'watchlist',
    proTip:
      'Review your Watchlist 15 minutes before market close (3:15 PM IST) to catch stocks closing strongly with heavy volume for BTST (Buy Today, Sell Tomorrow) or swing trades.',
  },

  '/bulk-block-deals': {
    path: '/bulk-block-deals',
    title: 'Bulk & Block Deals',
    shortTitle: 'Bulk Deals',
    group: 'Research',
    subtitle: 'Live transaction tape of large marquee institutional, promoter, and high-net-worth fund transactions.',
    whatItRepresents:
      'Large institutional transactions reported directly to exchange authorities (transactions exceeding 0.5% of total equity shares or >₹10 Cr). Shows marquee funds, domestic mutual funds, foreign portfolio investors (FPIs), and promoters executing significant stakes.',
    whyItMatters:
      'Bulk and block deals represent the rawest, unvarnished form of institutional capital allocation. When marquee investors buy significant equity stakes, it often triggers multi-month valuation re-ratings.',
    keyMetrics: [
      {
        label: 'Deal Type (Bulk vs. Block)',
        interpretation: 'Block deals occur during a dedicated exchange trading window between institutional participants; Bulk deals occur on the regular market tape.',
      },
      {
        label: 'Trade Value (₹ Cr) & Quantity',
        interpretation: 'Total size of the transaction, indicating fund commitment level.',
      },
      {
        label: 'Client / Fund Name',
        interpretation: 'Reveals who bought or sold—marquee institutional funds (e.g. Goldman Sachs, SBI Mutual Fund) vs. corporate promoters.',
      },
    ],
    actionablePlaybook: [
      'Pay special attention when a tier-1 institutional fund initiates a fresh bulk purchase in a mid or small-cap stock.',
      'Check whether the transaction was a buy or sell: multiple institutional funds absorbing shares sold by a promoter is a strongly bullish signal.',
    ],
    playbookSectionId: 'bulk-block-deals',
    proTip:
      'If an institutional fund buys a large stake in a bulk deal and the stock closes above the deal price, the deal price often acts as unbreakable institutional support on future pullbacks.',
  },

  '/corporate-feeds': {
    path: '/corporate-feeds',
    title: 'Corporate Feeds',
    shortTitle: 'Feeds',
    group: 'Research',
    subtitle: 'Live, AI-enriched exchange disclosures for order wins, capacity expansions, buybacks, and corporate catalysts.',
    whatItRepresents:
      'Exchange-reported regulatory filings and corporate catalysts, enriched with AI intelligence. Parses regulatory announcements under Regulation 30 to extract material details: contract order values, client counterparties, capacity additions, and joint ventures.',
    whyItMatters:
      'Explains the fundamental "Why" behind sudden volume shockers and breakout surges. Disclosures often precede major institutional accumulation cycles.',
    keyMetrics: [
      {
        label: 'Deal Value / Amount',
        interpretation: 'Verified deal or project value normalized into Crores (₹ Cr) or Millions/Billions ($ M / $ Bn).',
      },
      {
        label: 'Order Category / Catalyst Tag',
        interpretation: 'Categorizes filings into Order Win, Expansion, Acquisition, Buyback, or Strategic Alliance.',
      },
      {
        label: 'Counterparty / Client',
        interpretation: 'Verified client entity awarding the contract or partner entering the joint venture.',
      },
    ],
    actionablePlaybook: [
      'Cross-reference new corporate announcements with the Volume Shockers list to see which disclosures are triggering genuine institutional buying.',
      'Check if the contract value is significant relative to the company’s annual revenue (e.g. an order worth 50%+ of trailing 12-month revenue).',
    ],
    playbookSectionId: 'corporate-feeds',
    proTip:
      'When a mid-cap stock wins an order exceeding 30% of its annual revenue and volume expands with 70%+ delivery, it frequently initiates a powerful multi-month uptrend.',
  },

  '/results-calendar': {
    path: '/results-calendar',
    title: 'Results Calendar',
    shortTitle: 'Results',
    group: 'Research & Earnings',
    subtitle: 'Upcoming and recent quarterly corporate earnings releases, board meetings, and financial scorecard disclosures.',
    whatItRepresents:
      'Schedule of board meetings for quarterly financial results announcements across NSE-listed equities.',
    whyItMatters:
      'Earnings events carry binary risk. Knowing exact release dates prevents entering new breakout positions right before volatility spikes and earnings surprises.',
    keyMetrics: [
      {
        label: 'Board Meeting Date',
        interpretation: 'Scheduled date when financial results will be considered and approved by the board of directors.',
      },
      {
        label: 'Market Cap Category',
        interpretation: 'Large, Mid, Small, or Micro cap designation to gauge liquidity and expected post-earnings volatility.',
      },
      {
        label: 'Pre-Earnings Technical Trend',
        interpretation: 'Stock structure (Above 21 EMA / Near 52W High) leading into the numbers.',
      },
    ],
    actionablePlaybook: [
      'Check earnings dates before opening swing positions; avoid entering full-size positions 1–2 days prior to earnings release.',
      'Monitor post-earnings reactions for EPS Gap-Ups: stocks that gap up 5%+ on massive volume after strong results often offer high-probability continuation setups.',
    ],
    playbookSectionId: 'calendar',
    proTip:
      'Stocks that consolidate tightly right into an earnings announcement and then gap up on 3x average volume have the highest institutional continuation probability.',
  },

  '/concall-calendar': {
    path: '/concall-calendar',
    title: 'Earnings Concalls Desk',
    shortTitle: 'Concalls',
    group: 'Research & Earnings',
    subtitle: 'Comprehensive calendar of earnings conference calls, investor presentations, and analyst transcripts.',
    whatItRepresents:
      'Scheduled investor conference calls and analyst presentations following quarterly earnings releases.',
    whyItMatters:
      'Management commentary during concalls reveals order book updates, capex plans, margin guidance, and sector demand trends before they show up in trailing financial metrics.',
    keyMetrics: [
      {
        label: 'Call Date & Time',
        interpretation: 'Exact scheduled investor call timing with dial-in details.',
      },
      {
        label: 'Management Guidance Tone',
        interpretation: 'Qualitative tone regarding future revenue growth, margin trajectory, and capacity utilization.',
      },
    ],
    actionablePlaybook: [
      'Track commentary from market leaders in a sector to predict demand trends for the broader peer group.',
      'Review concall audio or transcripts for management commentary on order book accretion and export demand.',
    ],
    playbookSectionId: 'concalls',
    proTip:
      'Management commentary often explains post-earnings stock price direction even when headline numbers appear to beat or miss street consensus.',
  },

  '/earnings-calendar': {
    path: '/earnings-calendar',
    title: 'Earnings Concalls Desk',
    shortTitle: 'Concalls',
    group: 'Research & Earnings',
    subtitle: 'Comprehensive calendar of earnings conference calls, investor presentations, and analyst transcripts.',
    whatItRepresents:
      'Scheduled investor conference calls and analyst presentations following quarterly earnings releases.',
    whyItMatters:
      'Management commentary during concalls reveals order book updates, capex plans, margin guidance, and sector demand trends.',
    keyMetrics: [
      {
        label: 'Call Date & Time',
        interpretation: 'Exact scheduled investor call timing with dial-in details.',
      },
    ],
    actionablePlaybook: [
      'Track commentary from market leaders in a sector to predict demand trends for the broader peer group.',
    ],
  },

  '/admin': {
    path: '/admin',
    title: 'Admin Control Panel',
    shortTitle: 'Control Panel',
    group: 'Administration',
    subtitle: 'Unified administrator control panel for user permissions, platform configuration, and festive celebration settings.',
    whatItRepresents:
      'Administrative desk managing user roles, subscription entitlements, and global platform configuration.',
    whyItMatters:
      'Ensures secure RBAC access governance, audit logging, and centralized platform-wide feature administration.',
    keyMetrics: [
      {
        label: 'Active Users',
        interpretation: 'Total registered user accounts with verified credentials.',
      },
      {
        label: 'Role Permissions',
        interpretation: 'Admin versus standard subscriber authorization tiers.',
      },
      {
        label: 'Platform Controls',
        interpretation: 'Global festive themes, live broadcast controls, and real-time sync.',
      },
    ],
    actionablePlaybook: [
      'Manage user roles and audit member access across the platform.',
      'Control global festive celebration modes and broadcast states.',
    ],
  },

  '/admin/users': {
    path: '/admin/users',
    title: 'Admin Control Panel',
    shortTitle: 'Control Panel',
    group: 'Administration',
    subtitle: 'Unified administrator control panel for user permissions, platform configuration, and festive celebration settings.',
    whatItRepresents:
      'Administrative desk managing user roles, subscription entitlements, and global platform configuration.',
    whyItMatters:
      'Ensures secure RBAC access governance, audit logging, and centralized platform-wide feature administration.',
    keyMetrics: [
      {
        label: 'Active Users',
        interpretation: 'Total registered user accounts with verified credentials.',
      },
      {
        label: 'Role Permissions',
        interpretation: 'Admin versus standard subscriber authorization tiers.',
      },
      {
        label: 'Platform Controls',
        interpretation: 'Global festive themes, live broadcast controls, and real-time sync.',
      },
    ],
    actionablePlaybook: [
      'Manage user roles and audit member access across the platform.',
      'Control global festive celebration modes and broadcast states.',
    ],
  },

  '/guide': {
    path: '/guide',
    title: 'Trading Playbook',
    shortTitle: 'Playbook',
    group: 'Support',
    subtitle: 'Comprehensive institutional trading manual, visual legends, indicator formulas, and systematic trading routines.',
    whatItRepresents:
      'The comprehensive master knowledge base for NiftySutra. Contains in-depth documentation of institutional delivery methodology, moving average color coding, screener mechanics, and daily trading routines.',
    whyItMatters:
      'Provides the structured methodology needed to trade with disciplined institutional rules rather than emotional impulses.',
    keyMetrics: [
      {
        label: 'Accumulation Criteria',
        interpretation: 'Volume > 30-day average volume AND Demat Delivery % ≥ 50%.',
      },
      {
        label: 'Distribution Criteria',
        interpretation: 'Volume > 30-day average volume AND Demat Delivery % < 40%.',
      },
    ],
    actionablePlaybook: [
      'Read through the Core Philosophy and Daily Routines sections before starting live trading.',
      'Refer to the Visual Legend whenever you encounter unfamiliar badge or row colors in the terminal.',
    ],
    playbookSectionId: 'core-philosophy',
    proTip:
      'Use the search bar at the top of the Playbook to quickly jump to any concept, formula, or indicator explanation.',
  },

  '/contact-us': {
    path: '/contact-us',
    title: 'Contact Us',
    shortTitle: 'Contact',
    group: 'Support',
    subtitle: 'Support desk and direct communication channel for feedback, inquiries, and platform assistance.',
    whatItRepresents:
      'Direct support channel to contact the NiftySutra engineering and research team for bug reports, methodology queries, or feature suggestions.',
    whyItMatters:
      'Ensures continuous refinement of the platform based on feedback from active market participants.',
    keyMetrics: [
      {
        label: 'Support Response',
        interpretation: 'Inquiries are typically reviewed within 24 to 48 market hours.',
      },
    ],
    actionablePlaybook: [
      'Submit feedback, questions on indicator interpretations, or reporting of data anomalies.',
    ],
  },
};

/**
 * Helper to resolve page info by current pathname
 */
export const getPageInfo = (pathname: string): PageInfoConfig => {
  // Exact match
  if (PAGE_INFO_MAP[pathname]) {
    return PAGE_INFO_MAP[pathname];
  }

  // Stock history path: /history/SYMBOL
  if (pathname.startsWith('/history/')) {
    const symbol = pathname.split('/history/')[1]?.toUpperCase() || 'STOCK';
    return {
      ...PAGE_INFO_MAP['/history'],
      title: `Stock Explorer: ${symbol}`,
      subtitle: `Historical delivery volume trends, moving average posture, and institutional accumulation ledger for ${symbol}.`,
    };
  }

  // Sector drilldown path: /sector/SECTOR
  if (pathname.startsWith('/sector/')) {
    const sec = decodeURIComponent(pathname.split('/sector/')[1] || '').trim() || 'SECTOR';
    return {
      ...PAGE_INFO_MAP['/sector'],
      title: `Sector Explorer: ${sec}`,
      subtitle: `Institutional drilldown tape, benchmark chart, sub-sector distribution, and constituent stock ledger for ${sec}.`,
    };
  }

  // Opportunities sub-routes
  if (pathname.startsWith('/opportunities')) {
    return PAGE_INFO_MAP['/opportunities'];
  }

  // Fallback defaults
  return {
    path: pathname,
    title: 'NiftySutra Terminal',
    shortTitle: 'Analytics',
    group: 'Market Intelligence',
    subtitle: 'Institutional delivery volume and market breadth terminal for National Stock Exchange (NSE) equities.',
    whatItRepresents:
      'NiftySutra isolates the institutional footprint across 2,000+ NSE equities using proprietary Demat delivery volume metrics, moving average breadth gauges, and sector rotation tracking.',
    whyItMatters:
      'Institutions move markets. Tracking Demat delivery volume separates genuine accumulation from speculative intraday noise.',
    keyMetrics: [
      {
        label: 'Demat Delivery Volume',
        interpretation: 'Shares permanently transferred to demat accounts (T+1 settlement), reflecting true long-term institutional ownership.',
      },
    ],
    actionablePlaybook: [
      'Always trade in the direction of the institutional tide: focus on stocks experiencing confirmed accumulation.',
    ],
    playbookSectionId: 'core-philosophy',
  };
};
